import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import config from '../config';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import { isFuzzyMatch } from '../shared/businessMatching';

const MultiStepForm = ({ savedAnalysisResults = null, savedBusinessData = null, isViewingReport = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // TEMPORARY FLAG: Set to true for online-only businesses that don't need zip code
  const IS_ONLINE_ONLY = false; // TODO: Make this dynamic based on business type or user selection

  // Helper function to check if user has purchased
  const checkPurchaseStatus = () => {
    // If viewing a saved report, user has already paid
    if (isViewingReport) {
      return true;
    }

    // Check if payment gate bypass is enabled (dev mode)
    if (config.BYPASS_PAYMENT_GATE) {
      console.log('🔓 Payment gate bypassed - Development mode active');
      return true;
    }

    // Normal purchase check
    return sessionStorage.getItem('hasPurchased') === 'true';
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    businessName: '',
    zipCode: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [businessData, setBusinessData] = useState(savedBusinessData);

  // Modal state for payment gate
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [modalContent, setModalContent] = useState({
    feature: '',
    functionality: ''
  });

  // Paywall overlay state for analysis view
  const [showPaywallOverlay, setShowPaywallOverlay] = useState(false);
  const [userQueries, setUserQueries] = useState([]);

  const [allQueries, setAllQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(isViewingReport);
  const [analysisResults, setAnalysisResults] = useState(savedAnalysisResults);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [queriesAnimationComplete, setQueriesAnimationComplete] = useState(false);
  const [isMockData, setIsMockData] = useState(false);
  const [showProfileBubbles, setShowProfileBubbles] = useState(false); // Will be set to true when queries load
  
  // State for expanded visibility score sections
  const [expandedVisibilityScores, setExpandedVisibilityScores] = useState({
    openai: false,
    gemini: false,
    grok: false,
    perplexity: false
  });
  
  // State for content rendering (delayed render for performance)
  // eslint-disable-next-line no-unused-vars
  const [renderContent, setRenderContent] = useState({
    openai: false,
    gemini: false,
    grok: false,
    perplexity: false
  });
  
  // State for expanded citation domains
  const [expandedCitationDomains, setExpandedCitationDomains] = useState({
    openai: null,
    gemini: null,
    grok: null,
    perplexity: null
  });
  
  // State for showing all URLs in expanded domains
  const [showAllUrls, setShowAllUrls] = useState({});
  
  // Track expanded responses for mobile view
  const [expandedResponses, setExpandedResponses] = useState({
    openai: {},
    gemini: {},
    grok: {},
    perplexity: {}
  });
  
  // Refs for positioning
  const buttonContainerRef = useRef(null);
  const queriesContainerRef = useRef(null);
  const scrollPositionRef = useRef(0); // Store scroll position before keyboard opens
  
  // Responsive state
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );

  // Initialize state from saved report data if provided
  useEffect(() => {
    if (isViewingReport && savedAnalysisResults && savedBusinessData) {
      setAnalysisResults(savedAnalysisResults);
      setBusinessData(savedBusinessData);
      setShowAnalysis(true);
      setLoadingAnalysis(false);
    }
  }, [isViewingReport, savedAnalysisResults, savedBusinessData]);

  // Check for paid analysis flag from post-checkout flow
  useEffect(() => {
    // Only run on home route (/)
    if (location.pathname !== '/') {
      return;
    }

    const runPaidAnalysis = sessionStorage.getItem('runPaidAnalysis');
    const businessDataStr = sessionStorage.getItem('businessData');
    const userEmail = sessionStorage.getItem('userEmail');

    if (runPaidAnalysis === 'true' && businessDataStr) {
      // Clear the flags immediately to prevent re-running
      sessionStorage.removeItem('runPaidAnalysis');
      sessionStorage.removeItem('userEmail');

      const businessData = JSON.parse(businessDataStr);
      setBusinessData(businessData);

      // Generate 30 queries and run analysis
      const runPaidAnalysisFlow = async () => {
        try {
          setShowResults(true);
          setShowAnalysis(true);
          setLoadingAnalysis(true);

          // Generate 30 queries
          const queriesResponse = await axios.post(`${config.API_URL}/api/generate-queries`, {
            businessData,
            numQueries: 30
          });

          const queries30 = queriesResponse.data.queries;
          setAllQueries(queries30);

          // Run analysis with 30 queries
          const analysisResponse = await axios.post(`${config.API_URL}/api/analyze-chatbots`, {
            queries: queries30,
            businessData,
            isMockData: false
          });

          const analysisResults = analysisResponse.data;
          setAnalysisResults(analysisResults);
          setLoadingAnalysis(false);

          // Save report to S3
          const saveResponse = await axios.post(`${config.API_URL}/api/save-report`, {
            email: userEmail,
            businessData,
            analysisResults
          });

          const { reportId } = saveResponse.data;

          // Clear sessionStorage and navigate to the saved report
          sessionStorage.removeItem('businessData');

          setTimeout(() => {
            navigate(`/analysis/${reportId}`);
          }, 1000); // Brief delay to show analysis results before navigating

        } catch (error) {
          console.error('Error running paid analysis:', error);
          setLoadingAnalysis(false);
          alert('An error occurred while generating your report. Please contact support.');
        }
      };

      runPaidAnalysisFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Re-run if pathname changes

  // Sync state with URL parameters (for browser back/forward navigation)
  useEffect(() => {
    // Only sync URL parameters on the home route
    if (location.pathname !== '/') {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const stepParam = urlParams.get('step');
    const viewParam = urlParams.get('view');
    const fieldParam = urlParams.get('field');

    // Handle field parameter (pre-fill business name)
    if (fieldParam && !formData.businessName) {
      setFormData(prev => ({
        ...prev,
        businessName: decodeURIComponent(fieldParam)
      }));
    }

    // Sync step and view state with URL
    if (viewParam === 'analysis') {
      // Navigate to analysis view
      if (analysisResults && businessData) {
        setShowAnalysis(true);
        setShowResults(true);
      } else {
        // If no data, go back to results or form
        if (businessData) {
          window.history.replaceState({}, '', '/?view=results');
        } else {
          window.history.replaceState({}, '', '/');
        }
      }
    } else if (viewParam === 'results') {
      // Navigate to results view
      if (businessData) {
        setShowResults(true);
        setShowAnalysis(false);
      } else {
        // If no business data, go back to form
        window.history.replaceState({}, '', '/');
        setShowResults(false);
      }
    } else if (stepParam) {
      // Handle step navigation
      const step = parseInt(stepParam, 10);
      if (step >= 1 && step <= 2) {
        setCurrentStep(step);
        setShowResults(false);
        setShowAnalysis(false);
      }
    } else if (!isViewingReport) {
      // No parameters - only reset to step 1 if not viewing a report
      // and only if we're not already in a results/analysis state
      if (!showResults && !showAnalysis) {
        setCurrentStep(1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]); // Re-run when URL changes

  // Handle viewport changes
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    setIsMobile(width <= 768);
    setIsTablet(width > 768 && width <= 1024);
    setViewportHeight(height);
  }, []);

  // Add resize listener
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [handleResize]);

  // Generate bubble data with memoization to prevent recalculation
  const bubbleData = useMemo(() => {
    if (!showProfileBubbles) return null;
    
    const randomSeed = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };
    
    return [...Array(10)].map((_, index) => {
        const delay = randomSeed(index * 1.3) * 2.5;
        const duration = 10 + randomSeed(index * 1.7) * 4;
        const startX = 5 + (index * 9) + randomSeed(index * 2.1) * 5;
        
        // Generate drift paths
        const curveType = randomSeed(index * 2.3);
        let drift1, drift2, drift3, drift4;
        
        if (curveType < 0.33) {
          const amplitude = 3 + randomSeed(index * 3.2) * 4;
          drift1 = amplitude;
          drift2 = -amplitude * 1.2;
          drift3 = amplitude * 0.8;
          drift4 = -amplitude * 0.5;
        } else if (curveType < 0.66) {
          const dir = randomSeed(index * 4.5) > 0.5 ? 1 : -1;
          const maxDrift = 4 + randomSeed(index * 5.2) * 4;
          drift1 = dir * maxDrift * 0.3;
          drift2 = dir * maxDrift * 0.6;
          drift3 = dir * maxDrift * 0.85;
          drift4 = dir * maxDrift;
        } else {
          drift1 = (randomSeed(index * 3.2) - 0.5) * 6;
          drift2 = (randomSeed(index * 4.5) - 0.5) * 8;
          drift3 = (randomSeed(index * 5.8) - 0.5) * 7;
          drift4 = (randomSeed(index * 6.9) - 0.5) * 6;
        }
        
        return {
          delay,
          duration,
          startX,
          drift1,
          drift2,
          drift3,
          drift4,
          size: 45 + randomSeed(index * 7.3) * 15
        };
    });
  }, [showProfileBubbles]);

  // Check for mock data parameter on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mockFile = urlParams.get('mockFile');
    const mock = urlParams.get('mock');
    
    if (mockFile) {
      // Load mock data from specified file
      setIsMockData(true);
      loadMockDataFromFile(mockFile);
    } else if (mock === 'true') {
      setIsMockData(true);
      // Use default mock data path
      loadMockDataFromFile('latest');
    }
  }, []);

  // Track visual viewport for mobile keyboards and browser UI chrome changes
  useEffect(() => {
    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!visualViewport) return;

    const updateFromVisualViewport = () => {
      const height = visualViewport.height || window.innerHeight;
      setViewportHeight(height);
    };

    visualViewport.addEventListener('resize', updateFromVisualViewport);
    visualViewport.addEventListener('scroll', updateFromVisualViewport);

    return () => {
      visualViewport.removeEventListener('resize', updateFromVisualViewport);
      visualViewport.removeEventListener('scroll', updateFromVisualViewport);
    };
  }, []);

  // Adjust container height for mobile
  useEffect(() => {
    if (isMobile) {
      document.documentElement.style.setProperty('--vh', `${viewportHeight * 0.01}px`);
    }
  }, [isMobile, viewportHeight]);

  // Reset animation state when returning to Results View
  useEffect(() => {
    if (showResults && !showAnalysis) {
      // Reset the animation complete state when coming back to results view
      // This prevents buttons from showing before queries animate in
      if (!userQueries || userQueries.length === 0) {
        setQueriesAnimationComplete(false);
      }
    }
  }, [showResults, showAnalysis, userQueries]);

  // Position button container dynamically
  useEffect(() => {
    if (queriesAnimationComplete && buttonContainerRef.current) {
      const positionButtons = () => {
        if (!buttonContainerRef.current) return;
        
        const viewportHeight = window.innerHeight;
        let finalY;
        
        // Check if queries container exists and has content
        if (queriesContainerRef.current) {
          const queriesRect = queriesContainerRef.current.getBoundingClientRect();
          // Calculate position: right under queries list or at bottom of viewport (whichever is higher)
          const underQueriesY = queriesRect.bottom + 20;
          const bottomOfViewportY = viewportHeight - 80; // 80px from bottom
          finalY = Math.min(underQueriesY, bottomOfViewportY);
        } else {
          // If no queries container, position at bottom of viewport
          finalY = viewportHeight - 80;
        }
        
        buttonContainerRef.current.style.top = `${finalY}px`;
      };
      
      // Position initially
      positionButtons();
      
      // Reposition on resize
      window.addEventListener('resize', positionButtons);
      return () => window.removeEventListener('resize', positionButtons);
    }
  }, [queriesAnimationComplete, userQueries.length]);

  // Trigger paywall overlay after 7 seconds in analysis view (if user hasn't paid)
  useEffect(() => {
    if (showAnalysis && !loadingAnalysis && analysisResults) {
      // Check if user has already paid
      const hasPaid = checkPurchaseStatus();

      if (!hasPaid && !isMockData) {
        // Show paywall overlay after 7 seconds
        const timer = setTimeout(() => {
          setShowPaywallOverlay(true);
        }, 15000);

        return () => clearTimeout(timer);
      }
    } else {
      // Reset paywall overlay when leaving analysis view
      setShowPaywallOverlay(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAnalysis, loadingAnalysis, analysisResults, isMockData]);

  // Helper function for fuzzy matching business names

  // Helper function to get sentiment class based on score
  const getSentimentClass = (score) => {
    if (!score || score === 'N/A') return 'neutral';
    if (score >= 70) return 'positive';
    if (score >= 40) return 'neutral';
    return 'negative';
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1 && !formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    // Skip zip code validation for online-only businesses
    if (step === 2 && !IS_ONLINE_ONLY) {
      const zipRegex = /^\d{5}(-\d{4})?$/;
      if (!formData.zipCode.trim()) {
        newErrors.zipCode = 'Zip code is required';
      } else if (!zipRegex.test(formData.zipCode.trim())) {
        newErrors.zipCode = 'Please enter a valid US zip code';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to update URL with current state
  const updateURL = useCallback((step = null, view = null) => {
    const params = new URLSearchParams(location.search);

    // Remove existing step/view params
    params.delete('step');
    params.delete('view');

    // Add new params if provided
    if (view) {
      params.set('view', view);
    } else if (step) {
      params.set('step', step.toString());
    }

    // Update URL without reloading page
    const newSearch = params.toString();
    const newURL = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
    window.history.pushState({}, '', newURL);
  }, [location]);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Skip zip code step for online-only businesses
      if (currentStep === 1 && IS_ONLINE_ONLY) {
        handleSubmit();
      } else if (currentStep < 2) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        updateURL(nextStep); // Update URL when moving to next step
      } else {
        handleSubmit();
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleNext();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      let businessInfo;
      
      if (IS_ONLINE_ONLY) {
        // For online-only businesses, skip business lookup and go directly to queries
        businessInfo = {
          name: formData.businessName,
          isOnlineOnly: true
        };
      } else {
        // Normal flow for location-based businesses
        const response = await axios.post(`${config.API_URL}/api/business-lookup`, {
          businessName: formData.businessName,
          zipCode: formData.zipCode.trim()
        });
        businessInfo = response.data;
      }
      
      setBusinessData(businessInfo);
      setLoading(false);

      // Small delay for smooth transition
      setTimeout(() => {
        setShowResults(true);
        updateURL(null, 'results'); // Update URL when showing results
        // Fetch user queries after showing results
        fetchUserQueries(businessInfo);
      }, 300);
    } catch (error) {
      console.error('Error looking up business:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Failed to lookup business. Please try again.' 
      });
      setLoading(false);
    }
  };

  // Helper function to format business type for display
  const formatBusinessType = (businessType, businessTypeDisplay) => {
    if (businessTypeDisplay) {
      return businessTypeDisplay;
    }
    if (businessType) {
      // Convert snake_case to Title Case
      return businessType
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    return 'places';
  };

  const fetchUserQueries = async (businessInfo) => {
    setLoadingQueries(true);
    setShowProfileBubbles(true);
    console.log('Starting profile bubbles animation');
    
    // Hide bubbles after 8 seconds
    setTimeout(() => {
      setShowProfileBubbles(false);
      console.log('Hiding profile bubbles');
    }, 8000);
    
    try {
      const response = await axios.post(`${config.API_URL}/api/generate-queries`, {
        businessData: businessInfo,
        nonlocal: IS_ONLINE_ONLY
      });
      
      if (response.data && response.data.queries) {
        // Store all queries for later analysis
        setAllQueries(response.data.queries);

        // Take only the first 6 queries to display
        const queriesToShow = response.data.queries.slice(0, 6);
        
        // Wait for bubbles to finish before showing queries (12 seconds)
        setTimeout(() => {
          // Set all queries at once - CSS handles the staggered animation
          setUserQueries(queriesToShow);
          
          // Calculate when all animations will be complete
          // Each query has 0.3s delay between them, plus 0.6s for the animation itself
          const totalAnimationTime = (queriesToShow.length - 1) * 300 + 600;
          
          // Mark animations as complete after all queries have animated in
          setTimeout(() => {
            setQueriesAnimationComplete(true);
          }, totalAnimationTime + 500);
        }, 8000); // Wait 8 seconds for bubbles to finish
        
        // Log how many queries were generated
        console.log(`Generated ${response.data.queries.length} queries, showing 6`);
        
        // Analysis will be triggered manually by user clicking "Run Analysis" button
      }
    } catch (error) {
      console.error('Error generating queries:', error);
      // Use fallback queries if API fails
      const formattedBusinessType = formatBusinessType(businessInfo.businessType, businessInfo.businessTypeDisplay);
      const fallbackQueries = [
        `Best ${formattedBusinessType} in ${businessInfo.zipCode}`,
        `${businessInfo.name} reviews`,
        `${businessInfo.name} hours and location`,
        `Places like ${businessInfo.name} nearby`,
        `Is ${businessInfo.name} open now?`
      ];
      // Wait for bubbles to finish before showing fallback queries (12 seconds)
      setTimeout(() => {
        // Set all fallback queries at once - CSS handles the staggered animation
        setUserQueries(fallbackQueries);
        
        // Calculate when all animations will be complete
        // Each query has 0.3s delay between them, plus 0.6s for the animation itself
        const totalAnimationTime = (fallbackQueries.length - 1) * 300 + 600;
        
        // Mark animations as complete after all queries have animated in
        setTimeout(() => {
          setQueriesAnimationComplete(true);
        }, totalAnimationTime + 500);
      }, 8000); // Wait 8 seconds for bubbles to finish
    } finally {
      setTimeout(() => {
        setLoadingQueries(false);
      }, 3000);
    }
  };



  const handleInputChange = (field, value) => {
    // Trim whitespace for zipCode field
    const processedValue = field === 'zipCode' ? value.trim() : value;
    setFormData({ ...formData, [field]: processedValue });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };
  
  // Handle input focus - store scroll position before keyboard appears
  const handleInputFocus = () => {
    if (isMobile) {
      scrollPositionRef.current = window.scrollY;
      console.log('Keyboard opening, stored scroll position:', scrollPositionRef.current);
    }
  };
  
  // Handle input blur - restore scroll position after keyboard hides
  const handleInputBlur = () => {
    if (isMobile) {
      // Small delay to allow keyboard to fully hide
      setTimeout(() => {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: 'smooth'
        });
        console.log('Keyboard hidden, restored scroll position:', scrollPositionRef.current);
      }, 300);
    }
  };

  // Function to load mock data from file
  const loadMockDataFromFile = async (filename) => {
    try {
      let mockDataPath;
      
      if (filename === 'latest') {
        // Try to fetch the latest file from the output directory
        mockDataPath = '/mock-data/latest.json';
      } else if (filename.startsWith('http')) {
        // Full URL provided
        mockDataPath = filename;
      } else {
        // Assume it's a filename in the mock-data directory
        mockDataPath = `/mock-data/${filename}`;
      }
      
      console.log('Loading mock data from:', mockDataPath);
      
      const response = await fetch(mockDataPath);
      if (!response.ok) {
        throw new Error(`Failed to load mock data: ${response.status}`);
      }
      
      const mockData = await response.json();

      // Handle different data formats
      let analysisData;

      if (mockData.chatbotResults) {
        // New format from scripts/output/*.json
        analysisData = mockData.chatbotResults;

        // Also load detailed responses if available
        if (mockData.detailedResponses) {
          analysisData.openai.detailedResponses = mockData.detailedResponses.openai || [];
          analysisData.gemini.detailedResponses = mockData.detailedResponses.gemini || [];
          analysisData.grok.detailedResponses = mockData.detailedResponses.grok || [];
          analysisData.perplexity.detailedResponses = mockData.detailedResponses.perplexity || [];
        }
      } else if (mockData.openai && mockData.gemini) {
        // Old format - data is already in the correct format
        analysisData = mockData;
      } else {
        console.error('Mock data format not recognized');
        return;
      }

      // Set the analysis results
      setAnalysisResults(analysisData);
      setShowAnalysis(true);

      // Set business data from metadata if it exists
      if (mockData.metadata) {
        setBusinessData({
          name: mockData.metadata.businessName || 'Mock Business',
          zipCode: mockData.metadata.zipCode || '94105',
          city: 'San Francisco',
          state: 'CA'
        });
        setFormData({
          businessName: mockData.metadata.businessName || 'Mock Business',
          zipCode: mockData.metadata.zipCode || '94105'
        });

        // Extract queries from detailedResponses if available
        if (mockData.detailedResponses && mockData.detailedResponses.openai) {
          const queries = mockData.detailedResponses.openai.map(item => item.query);
          setUserQueries(queries);
          setAllQueries(queries);
        }
      }

      // Skip to results
      setCurrentStep(2);
      setShowResults(true);

      console.log('Mock data loaded successfully from:', mockDataPath);
      console.log('Loaded analysis for:', mockData.metadata?.businessName || 'Unknown business');
    } catch (error) {
      console.error('Error loading mock data:', error);
      // Continue with normal flow if mock data fails to load
    }
  };

  const runChatbotAnalysis = async (queries, businessInfo) => {
    setShowAnalysis(true);
    setLoadingAnalysis(true);
    updateURL(null, 'analysis'); // Update URL when showing analysis
    
    try {
      const response = await axios.post(`${config.API_URL}/api/analyze-chatbots`, {
        queries: queries,
        businessData: businessInfo,
        isMockData: isMockData
      });
      
      if (response.data) {
        setAnalysisResults(response.data);
      }
    } catch (error) {
      console.error('Error running chatbot analysis:', error);
      // Mock results if API fails
      setAnalysisResults({
        openai: {
          visibilityScore: { mentioned: 18, total: 30, percentage: 60 },
          rankingScore: { rank: 2, totalCompetitors: 5 },
          competitors: ['Competitor A', 'Competitor B', 'Competitor C', 'Competitor D', 'Competitor E', 'Competitor F', 'Competitor G', 'Competitor H', 'Competitor I', 'Competitor J'],
          queryResponsePairs: [
            {
              query: "Best coffee shops near me",
              response: "I found several great coffee shops in your area:\n\n**Top Recommendations:**\n- **Starbucks** - Located at 123 Main St\n- **Joe's Coffee** - Local favorite at 456 Oak Ave\n\nBoth are highly rated by customers and offer excellent coffee. Joe's Coffee is particularly known for their artisanal blends and cozy atmosphere.\n\n*Note: Hours may vary, so check before visiting.*",
              mentioned: false
            },
            {
              query: "Local business recommendations",
              response: "Based on your location, here are my top picks:\n\n## 🏪 **Wendy's Bakery**\n- **Location:** 789 Pine St\n- **Specialties:** Pastries, bread, custom cakes\n- **Rating:** ⭐⭐⭐⭐⭐\n\n## 🔧 **Bob's Tools**\n- **Location:** 321 Elm St\n- **Specialties:** Hardware, DIY supplies\n\n> *Wendy's Bakery comes highly recommended for their excellent customer service and quality products.*",
              mentioned: true
            },
            {
              query: "Restaurants with outdoor seating",
              response: "For outdoor dining, consider these options:\n\n### Outdoor Dining Options:\n\n1. **Wendy's Bakery** 🍰\n   - Lovely garden patio\n   - Perfect for afternoon tea\n   - Reservations recommended\n\n2. **Bella Vista** 🍝\n   - Sidewalk seating\n   - Italian cuisine\n   - Great atmosphere\n\n```\nPro tip: Call ahead to check outdoor seating availability!\n```",
              mentioned: true
            }
          ]
        },
        gemini: {
          visibilityScore: { mentioned: 22, total: 30, percentage: 73 },
          rankingScore: { rank: 1, totalCompetitors: 4 },
          competitors: ['Competitor A', 'Competitor D', 'Competitor K', 'Competitor L', 'Competitor M', 'Competitor N', 'Competitor O', 'Competitor P', 'Competitor Q', 'Competitor R'],
          queryResponsePairs: [
            {
              query: "Where to buy fresh bread",
              response: "**Wendy's Bakery** is the best place for fresh bread in your area! 🥖\n\n## Why Choose Wendy's Bakery?\n- **Daily baking** - Fresh bread every morning\n- **Artisanal varieties** - Sourdough, rye, whole wheat\n- **Local favorite** - Highly rated by community\n\n### Popular Breads:\n1. **Sourdough** - Their signature loaf\n2. **Rye** - Perfect for sandwiches\n3. **Whole Wheat** - Healthy option\n\n> *Their sourdough is particularly popular among locals and sells out quickly!*\n\n**Location:** Check their website for current hours and availability.",
              mentioned: true
            },
            {
              query: "Local events this weekend",
              response: "Here's what's happening this weekend in your area:\n\n## 📅 **Weekend Events**\n\n### Saturday:\n- **Farmers Market** - Downtown, 9 AM - 2 PM\n- **Bread-Making Workshop** at Wendy's Bakery - 2 PM - 4 PM\n\n### Sunday:\n- **Community Garden Tour** - 11 AM - 1 PM\n- **Local Art Show** - Community Center, 3 PM - 6 PM\n\n## 🎯 **Featured Event:**\n**Wendy's Bakery Bread-Making Workshop**\n- Learn from expert bakers\n- Take home fresh bread\n- **Cost:** $25 per person\n- **Registration required**\n\n```\nDon't miss this unique opportunity to learn from the best!\n```",
              mentioned: true
            },
            {
              query: "Best places to work remotely",
              response: "Here are the top remote work spots in your area:\n\n### 🏢 **Co-working Spaces:**\n- **Main Street Co-Work** - Professional environment\n- **Innovation Hub** - Tech-focused space\n\n### 📚 **Public Spaces:**\n- **Public Library** - Quiet, free WiFi\n- **Community Center** - Spacious tables\n\n### ☕ **Café Options:**\n- **Wendy's Bakery** - Quiet corner with excellent coffee\n- **Local Coffee Shop** - Bustling atmosphere\n\n## 💡 **Pro Tips:**\n- Wendy's Bakery has a dedicated work area\n- Free WiFi available\n- Great coffee and pastries for fuel\n- **Best time:** Weekday mornings (less crowded)\n\n*Perfect for a change of scenery when working from home!*",
              mentioned: true
            }
          ]
        },
        grok: {
          visibilityScore: { mentioned: 15, total: 30, percentage: 50 },
          rankingScore: { rank: 3, totalCompetitors: 6 },
          competitors: ['Competitor B', 'Competitor E', 'Competitor F', 'Competitor S', 'Competitor T', 'Competitor U', 'Competitor V', 'Competitor W', 'Competitor X', 'Competitor Y'],
          queryResponsePairs: [
            {
              query: "Local business directory",
              response: "Here's a comprehensive list of local businesses in your area:\n\n## 🏪 **Local Business Directory**\n\n### **Food & Beverage:**\n- **Wendy's Bakery** - Artisanal breads and pastries\n- **ABC Hardware** - Tools and home improvement\n- **XYZ Pharmacy** - Prescriptions and health products\n\n### **Services:**\n- **Main Street Cleaners** - Dry cleaning and alterations\n- **Quick Fix Auto** - Auto repair and maintenance\n\n### **Retail:**\n- **Local Market** - Groceries and household items\n- **Book Nook** - Books and stationery\n\n> *Each business offers unique services to the community and contributes to the local economy.*",
              mentioned: true
            },
            {
              query: "Best pizza delivery",
              response: "For pizza delivery in your area, here are the top options:\n\n## 🍕 **Pizza Delivery Rankings**\n\n### **1. Pizza Palace** ⭐⭐⭐⭐⭐\n- **Delivery Time:** 20-30 minutes\n- **Specialties:** Traditional Italian pizza\n- **Price Range:** $12-$25\n\n### **2. Domino's** ⭐⭐⭐⭐\n- **Delivery Time:** 25-35 minutes\n- **Specialties:** Fast delivery, variety of toppings\n- **Price Range:** $10-$20\n\n### **3. Local Pizza Co.** ⭐⭐⭐\n- **Delivery Time:** 30-45 minutes\n- **Specialties:** Artisanal ingredients\n- **Price Range:** $15-$30\n\n**Note:** All offer online ordering and contactless delivery options.",
              mentioned: false
            },
            {
              query: "Where to get birthday cakes",
              response: "For birthday cakes, you have excellent options:\n\n## 🎂 **Birthday Cake Options**\n\n### **🥇 Wendy's Bakery** (Top Choice)\n- **Specialties:** Custom designs, various flavors\n- **Features:**\n  - Beautiful decorations\n  - Multiple size options\n  - Dietary accommodations\n  - 48-hour notice required\n\n### **🥈 Sweet Treats Bakery**\n- **Specialties:** Fondant cakes, novelty designs\n- **Features:**\n  - Intricate designs\n  - Premium ingredients\n  - 72-hour notice required\n\n### **🥉 Grocery Store Bakeries**\n- **Specialties:** Standard designs, quick availability\n- **Features:**\n  - Same-day options\n  - Budget-friendly\n  - Limited customization\n\n> *Wendy's Bakery is highly recommended for their quality and attention to detail!*",
              mentioned: true
            }
          ]
        }
      });
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const toggleVisibilityScoreExpansion = (chatbot) => {
    // Check if user has purchased before allowing expansion (skip for mock data)
    if (!checkPurchaseStatus() && !isMockData) {
      setModalContent({
        feature: `View all user chats`,
        functionality: `see what real users are asking about your business`
      });
      setShowPaymentModal(true);
      return;
    }
    
    const isCurrentlyExpanded = expandedVisibilityScores[chatbot];
    
    // Update expansion state immediately for instant animation
    setExpandedVisibilityScores({
      openai: false,
      gemini: false,
      grok: false,
      perplexity: false,
      [chatbot]: !isCurrentlyExpanded
    });
    
    // If we're closing, reset all expanded responses for this chatbot
    if (isCurrentlyExpanded) {
      setExpandedResponses(prev => ({
        ...prev,
        [chatbot]: {}
      }));
    }
    
    // Handle content rendering
    if (!isCurrentlyExpanded) {
      // Opening - set render content immediately
      setRenderContent({
        openai: false,
        gemini: false,
        grok: false,
        perplexity: false,
        [chatbot]: true
      });
    } else {
      // Closing - clear render content immediately
      setRenderContent(prevRender => ({
        ...prevRender,
        [chatbot]: false
      }));
    }
  };

  // Handle citation link clicks with payment check
  const handleCitationClick = (e, url, context = null) => {
    // Check if user has purchased before allowing navigation (skip for mock data)
    if (!checkPurchaseStatus() && !isMockData) {
      e.preventDefault();

      // Customize message based on context if provided
      let feature = 'Citation Sources';
      let functionality = 'access detailed source links and references';

      if (context) {
        if (context.includes('positive')) {
          feature = 'Positive Sentiment Sources';
          functionality = 'see where positive mentions come from';
        } else if (context.includes('negative')) {
          feature = 'Negative Sentiment Sources';
          functionality = 'identify and address negative feedback sources';
        } else if (context.includes('neutral')) {
          feature = 'Neutral Sentiment Sources';
          functionality = 'explore neutral discussion sources';
        }
      }

      setModalContent({
        feature,
        functionality
      });
      setShowPaymentModal(true);
      return false;
    }
    // Allow normal link behavior if purchased or viewing mock data
    return true;
  };

  // Custom ReactMarkdown components to handle link clicks
  const markdownComponents = {
    a: ({node, children, ...props}) => {
      return (
        <a
          {...props}
          onClick={(e) => handleCitationClick(e, props.href, 'markdown-content')}
        >
          {children}
        </a>
      );
    }
  };

  // Helper function to get full URLs with counts for a specific domain from annotations
  const getUrlsForDomain = (queryResponsePairs, domain) => {
    const urlCounts = {};
    
    if (!queryResponsePairs || !Array.isArray(queryResponsePairs)) {
      return [];
    }
    
    queryResponsePairs.forEach(pair => {
      if (pair.annotations && Array.isArray(pair.annotations)) {
        pair.annotations.forEach(annotation => {
          if (annotation.domain && annotation.domain.toLowerCase() === domain.toLowerCase() && annotation.url) {
            urlCounts[annotation.url] = (urlCounts[annotation.url] || 0) + 1;
          }
        });
      }
    });
    
    // Convert to array and sort by count (descending)
    return Object.entries(urlCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([url, count]) => ({ url, count }));
  };

  // Toggle citation domain expansion
  const toggleCitationDomainExpansion = (chatbot, domain) => {
    setExpandedCitationDomains(prev => {
      const currentExpanded = prev[chatbot];
      
      // If clicking on the same domain, collapse it
      if (currentExpanded === domain) {
        // Also reset the show all URLs state for this domain
        setShowAllUrls(prevUrls => {
          const key = `${chatbot}-${domain}`;
          const newState = { ...prevUrls };
          delete newState[key];
          return newState;
        });
        
        return {
          ...prev,
          [chatbot]: null
        };
      }
      
      // Otherwise, expand the clicked domain
      return {
        ...prev,
        [chatbot]: domain
      };
    });
  };
  
  // Toggle showing all URLs for a specific domain
  const toggleShowAllUrls = (chatbot, domain) => {
    const key = `${chatbot}-${domain}`;
    setShowAllUrls(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Format URL for display (make it more readable and user-friendly)
  const formatUrlForDisplay = (url) => {
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      
      // Remove www. prefix for cleaner look
      const cleanDomain = hostname.replace(/^www\./, '');
      
      // Extract the main domain name (e.g., "forbes" from "forbes.com")
      const domainParts = cleanDomain.split('.');
      
      // Handle subdomains specially (e.g., "docs.microsoft.com" -> "Microsoft Docs")
      if (domainParts.length > 2) {
        const subdomain = domainParts[0];
        const mainDomain = domainParts[1];
        
        // Common subdomain patterns
        if (subdomain === 'docs' || subdomain === 'documentation') {
          return `${mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1)} Docs`;
        }
        if (subdomain === 'blog') {
          return `${mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1)} Blog`;
        }
        if (subdomain === 'support' || subdomain === 'help') {
          return `${mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1)} Support`;
        }
        
        // For other subdomains, just show the main domain
        return mainDomain.charAt(0).toUpperCase() + mainDomain.slice(1);
      }
      
      // For standard domains (e.g., "forbes.com", "x.ai")
      if (domainParts.length >= 2) {
        const name = domainParts[0];
        
        // Special cases for short domains
        if (name.length <= 3) {
          return name.toUpperCase(); // e.g., "x" -> "X", "ai" -> "AI"
        }
        
        // Capitalize first letter of domain name
        return name.charAt(0).toUpperCase() + name.slice(1);
      }
      
      // Fallback to clean domain
      return cleanDomain;
      
    } catch {
      // If URL parsing fails, return a truncated version
      return url.length > 50 ? url.substring(0, 50) + '...' : url;
    }
  };

  // Responsive container class
  const getContainerClass = () => {
    if (isMobile) return 'typeform-container mobile';
    if (isTablet) return 'typeform-container tablet';
    return 'typeform-container';
  };

  // Render both form and results in same container for smooth transition
  return (
    <div className={getContainerClass()}>
      {/* Dev Mode Indicator */}
      {config.BYPASS_PAYMENT_GATE && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#ff6b35',
          color: 'white',
          padding: '8px 20px',
          fontSize: '14px',
          fontWeight: 'bold',
          zIndex: 9999,
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>🔓</span>
          <span>DEV MODE - Payment Gate Bypassed</span>
        </div>
      )}
      
      {/* Logo */}
      <div style={{
        position: 'fixed',
        top: isMobile ? '15px' : '20px',
        left: isMobile ? '15px' : '20px',
        zIndex: 1000
      }}>
        <img 
          src="/images/AppLogo@3x.png" 
          alt="NameDrop Logo"
          style={{
            height: isMobile ? '30px' : '40px', // Smaller logo on mobile
            width: 'auto'
          }}
        />
      </div>
      
      {/* Form View */}
      {!showResults && !showAnalysis && (
        <div className="typeform-content form-view" style={{
          marginTop: isMobile ? '60px' : '0px' // Add top margin on mobile to avoid logo overlap
        }}>

          <div className="form-wrapper">
            {currentStep === 1 && (
              <div className="step-container">
                <div className="step-number">1 →</div>
                <div className="question-content">
                  <h1 className="question-title">
                    What's your business name?
                  </h1>
                  <p className="question-subtitle">
                    {IS_ONLINE_ONLY ? 'Enter your online business name' : 'Let\'s start with the basics'}
                  </p>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className={`typeform-input ${errors.businessName ? 'error' : ''}`}
                      placeholder="Type your business name here"
                      value={formData.businessName}
                      onChange={(e) => handleInputChange('businessName', e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      autoFocus
                    />
                    {errors.businessName && (
                      <p className="error-message">{errors.businessName}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="step-container">
                <div className="step-number">2 →</div>
                <div className="question-content">
                  <h1 className="question-title">
                    What's your business zip code?
                  </h1>
                  <p className="question-subtitle">
                    This helps us provide location-specific information
                  </p>
                  <div className="input-wrapper">
                    <input
                      type="text"
                      className={`typeform-input ${errors.zipCode ? 'error' : ''}`}
                      placeholder="e.g., 10001"
                      value={formData.zipCode}
                      onChange={(e) => handleInputChange('zipCode', e.target.value)}
                      onKeyDown={handleKeyDown}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      autoFocus
                      maxLength="10"
                    />
                    {errors.zipCode && (
                      <p className="error-message">{errors.zipCode}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="button-container">
              <button 
                className="typeform-button"
                onClick={handleNext}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner"></span>
                    Looking up...
                  </>
                ) : (
                  <>
                    {(currentStep === 2 || (currentStep === 1 && IS_ONLINE_ONLY)) ? 'Submit' : 'OK ✓'}
                    {!isMobile && <span className="button-hint">press Enter ↵</span>}
                  </>
                )}
              </button>
            </div>
            
            {errors.submit && (
              <div className="submit-error">
                <p className="error-message">{errors.submit}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results View */}
      {showResults && businessData && !showAnalysis && (
        <div className="results-content">
          <div className="results-animation">
            <div className="pulse-ring"></div>
            <div className="pulse-ring delay-1"></div>
            <div className="pulse-ring delay-2"></div>
            <div className="search-icon">🔍</div>
          </div>
          
          {/* Profile Bubbles Animation */}
          {showProfileBubbles && bubbleData ? (
            <div className="profile-bubbles-container">
              {bubbleData.map((bubble, index) => {
                // All profile photos from the directory
                const profilePhotos = [
                  `/images/ProfilePhotos/1.jpg`,
                  `/images/ProfilePhotos/2.jpg`,
                  `/images/ProfilePhotos/3.jpg`,
                  `/images/ProfilePhotos/4.jpeg`,
                  `/images/ProfilePhotos/5.jpeg`,
                  `/images/ProfilePhotos/6.jpeg`,
                  `/images/ProfilePhotos/7.jpeg`,
                  `/images/ProfilePhotos/8.jpg`,
                  `/images/ProfilePhotos/9.jpg`,
                  `/images/ProfilePhotos/10.jpg`,
                  `/images/ProfilePhotos/11.jpg`,
                  `/images/ProfilePhotos/12.jpg`,
                  `/images/ProfilePhotos/13.jpg`
                ];
                
                return (
                  <div
                    key={index}
                    className={`profile-bubble bubble-random-${index}`}
                    style={{
                      '--bubble-delay': `${bubble.delay}s`,
                      '--bubble-duration': `${bubble.duration}s`,
                      '--start-x': `${bubble.startX}%`,
                      '--drift1': `${bubble.drift1}vw`,
                      '--drift2': `${bubble.drift2}vw`,
                      '--drift3': `${bubble.drift3}vw`,
                      '--drift4': `${bubble.drift4}vw`,
                      '--bubble-size': `${bubble.size}px`
                    }}
                  >
                    <img 
                      src={profilePhotos[index % profilePhotos.length]} 
                      alt="Profile" 
                      className="bubble-profile-image"
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
          
          <h1 className="results-title">
            Searching for what real users have asked AI about
          </h1>
          <h2 className="business-name-highlight">
            {businessData.name || formData.businessName}
          </h2>
          
          {(loadingQueries || showProfileBubbles) && userQueries.length === 0 && (
            <>
              <div className="loading-dots" style={{ display: 'flex', justifyContent: 'center' }}>
                <span></span>
                <span></span>
                <span></span>
              </div>
              <p className="results-subtitle">
                Analyzing AI chats and responses...
              </p>
            </>
          )}
          
          {userQueries.length > 0 && (
            <div className="queries-container" ref={queriesContainerRef}>
              <p className="queries-label">Found the following AI chats:</p>
              <div className="queries-list">
                {userQueries.map((query, index) => (
                  <div 
                    key={index} 
                    className="query-item"
                    style={{ animationDelay: `${index * 0.3}s` }}
                  >
                    <span className="query-icon">🔎</span>
                    <span className="query-text">{typeof query === 'string' ? query : query.query}</span>
                  </div>
                ))}
                {/* More queries indicator */}
                {userQueries.length > 0 && (
                  <div 
                    className="query-item more-indicator"
                    style={{ animationDelay: `${userQueries.length * 0.3}s` }}
                  >
                    <span style={{fontSize: '14px' }}>...</span>
                    <span className="query-text" style={{ color: 'rgba(255, 255, 255)', fontWeight: 'bold' }}>
                      and 1000+ more found
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {queriesAnimationComplete && showResults && !showAnalysis && (
            <div 
              ref={buttonContainerRef}
              style={{
              position: 'fixed',
              bottom: 'auto',
              left: '0',
              right: '0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              opacity: 0,
              animation: 'fadeInUp 0.8s ease-out 0.5s forwards',
              zIndex: 10,
              pointerEvents: 'none'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                maxWidth: '860px',
                padding: '0 20px',
                pointerEvents: 'auto'
              }}>
              <button 
                onClick={() => {
                  setShowResults(false);
                  setCurrentStep(1);
                  setFormData({ businessName: '', zipCode: '' });
                  setUserQueries([]);
                  setQueriesAnimationComplete(false);
                  setShowProfileBubbles(false);
                }}
                style={{ 
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: '10px 20px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '30px',
                  cursor: 'pointer'
                }}
              >
                Start Over
              </button>
              
              <button 
                onClick={() => {
                  // Don't reset showResults - keep it true to hide the form
                  setShowAnalysis(true);
                  runChatbotAnalysis(allQueries, businessData);
                }}
                style={{ 
                  background: 'rgba(255, 255, 255, 0.95)',
                  color: '#333',
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: '10px 24px',
                  border: '2px solid white',
                  borderRadius: '30px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '3px',
                  maxWidth: '250px',
                  whiteSpace: 'normal',
                  lineHeight: '1.3',
                  textAlign: 'left',
                  boxShadow: '0 0 20px rgba(255, 255, 255, 0.8)',
                  animation: 'pulseOutline 1.5s infinite'
                }}
              >
                <span style={{ wordBreak: 'break-word' }}>Next: See what AI is saying about your business</span>
                <span style={{ fontSize: '14px', flexShrink: 0 }}>→</span>
              </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Analysis View */}
      {showAnalysis && (
        <div className="analysis-content">
          <div className="analysis-header">
            <h1 className="analysis-title">
              Analyzing your business presence across thousands of AI chats and 4 different apps...
            </h1>
          </div>

          {loadingAnalysis && (
            <div className="analysis-loading">
              <div className="chatbot-icons-loading">
                <img 
                  src="/images/ChatGPTIcon.svg" 
                  alt="ChatGPT" 
                  className="chatbot-icon-loading"
                  style={{ animationDelay: '0s' }}
                />
                <img 
                  src="/images/GeminiIcon.svg" 
                  alt="Gemini" 
                  className="chatbot-icon-loading"
                  style={{ animationDelay: '0.5s' }}
                />
                <img 
                  src="/images/GrokIcon.png" 
                  alt="Grok" 
                  className="chatbot-icon-loading"
                  style={{ animationDelay: '1s' }}
                />
                <img 
                  src="/images/PerplexityIcon.svg" 
                  alt="Perplexity" 
                  className="chatbot-icon-loading"
                  style={{ 
                    animationDelay: '1.5s',
                    filter: 'brightness(0.3) contrast(1.5)'
                  }}
                />
              </div>
              <p className="loading-text" style={{ 
                fontSize: 'clamp(18px, 4vw, 24px)',
                fontWeight: '300',
                letterSpacing: '-0.5px',
                marginTop: '30px'
              }}>
                Analyzing responses from OpenAI, Gemini, Grok, and Perplexity.<br/>
                This will take around a minute...
              </p>
            </div>
          )}

          {!loadingAnalysis && analysisResults && (
            <div className="analysis-results">
              {/* Desktop Table View */}
              <div className="analysis-table-container desktop-view">
                <div className="analysis-table-wrapper">
                  <table className="analysis-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th className="chatbot-header-cell">
                        <div className="chatbot-header-content">
                          <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" className="chatbot-icon" />
                          <h3>ChatGPT</h3>
                        </div>
                      </th>
                      <th className="chatbot-header-cell">
                        <div className="chatbot-header-content">
                          <img src="/images/GeminiIcon.svg" alt="Gemini" className="chatbot-icon" />
                          <h3>Gemini</h3>
                        </div>
                      </th>
                      <th className="chatbot-header-cell">
                        <div className="chatbot-header-content">
                          <img src="/images/GrokIcon.png" alt="Grok" className="chatbot-icon" />
                          <h3>Grok</h3>
                        </div>
                      </th>
                      <th className="chatbot-header-cell">
                        <div className="chatbot-header-content">
                          <img src="/images/PerplexityIcon.svg" alt="Perplexity" className="chatbot-icon" />
                          <h3>Perplexity</h3>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Visibility Score Row */}
                    <tr className="table-row">
                      <td className="metric-label">
                        <strong>Visibility Score</strong>
                        <div className="metric-description">What percentage of AI chats mention your business?</div>
                      </td>
                      <td className="metric-cell">
                        <div className="visibility-score">
                          <div 
                            className="score-bar-container clickable"
                            onClick={() => toggleVisibilityScoreExpansion('openai')}
                            title={`Click to view ${analysisResults.openai?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div className="score-bar">
                              <div 
                                className="score-fill"
                                style={{ width: `${analysisResults.openai?.visibilityScore?.percentage || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="score-text">
                            <span className="score-percentage-large">{analysisResults.openai?.visibilityScore?.percentage || 0}%</span>
                          </div>
                          <div 
                            className="query-count-hint clickable"
                            onClick={() => toggleVisibilityScoreExpansion('openai')}
                            title={`Click to view ${analysisResults.openai?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div>{expandedVisibilityScores.openai ? 'Hide' : 'View chats and responses'}</div>
                            <div className={`expansion-icon ${expandedVisibilityScores.openai ? 'expanded' : ''}`}>▶</div>
                          </div>
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="visibility-score">
                          <div 
                            className="score-bar-container clickable"
                            onClick={() => toggleVisibilityScoreExpansion('gemini')}
                            title={`Click to view ${analysisResults.gemini?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div className="score-bar">
                              <div 
                                className="score-fill"
                                style={{ width: `${analysisResults.gemini?.visibilityScore?.percentage || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="score-text">
                            <span className="score-percentage-large">{analysisResults.gemini?.visibilityScore?.percentage || 0}%</span>
                          </div>
                          <div 
                            className="query-count-hint clickable"
                            onClick={() => toggleVisibilityScoreExpansion('gemini')}
                            title={`Click to view ${analysisResults.gemini?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div>{expandedVisibilityScores.gemini ? 'Hide' : 'View chats and responses'}</div>
                            <div className={`expansion-icon ${expandedVisibilityScores.gemini ? 'expanded' : ''}`}>▶</div>
                          </div>
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="visibility-score">
                          <div 
                            className="score-bar-container clickable"
                            onClick={() => toggleVisibilityScoreExpansion('grok')}
                            title={`Click to view ${analysisResults.grok?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div className="score-bar">
                              <div 
                                className="score-fill"
                                style={{ width: `${analysisResults.grok?.visibilityScore?.percentage || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="score-text">
                            <span className="score-percentage-large">{analysisResults.grok?.visibilityScore?.percentage || 0}%</span>
                          </div>
                          <div 
                            className="query-count-hint clickable"
                            onClick={() => toggleVisibilityScoreExpansion('grok')}
                            title={`Click to view ${analysisResults.grok?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div>{expandedVisibilityScores.grok ? 'Hide' : 'View chats and responses'}</div>
                            <div className={`expansion-icon ${expandedVisibilityScores.grok ? 'expanded' : ''}`}>▶</div>
                          </div>
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="visibility-score">
                          <div 
                            className="score-bar-container clickable"
                            onClick={() => toggleVisibilityScoreExpansion('perplexity')}
                            title={`Click to view ${analysisResults.perplexity?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div className="score-bar">
                              <div 
                                className="score-fill"
                                style={{ width: `${analysisResults.perplexity?.visibilityScore?.percentage || 0}%` }}
                              />
                            </div>
                          </div>
                          <div className="score-text">
                            <span className="score-percentage-large">{analysisResults.perplexity?.visibilityScore?.percentage || 0}%</span>
                          </div>
                          <div 
                            className="query-count-hint clickable"
                            onClick={() => toggleVisibilityScoreExpansion('perplexity')}
                            title={`Click to view ${analysisResults.perplexity?.queryResponsePairs?.length || 0} queries and responses`}
                          >
                            <div>{expandedVisibilityScores.perplexity ? 'Hide' : 'View chats and responses'}</div>
                            <div className={`expansion-icon ${expandedVisibilityScores.perplexity ? 'expanded' : ''}`}>▶</div>
                          </div>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Queries and Responses Rows */}
                    {Object.entries(expandedVisibilityScores).map(([chatbot, isExpanded]) => {
                      if (!isExpanded || !analysisResults?.[chatbot]?.queryResponsePairs) return null;
                      
                      return (
                        <tr key={`${chatbot}-expanded`} className="expanded-row">
                          <td colSpan="5" className="expanded-content">
                            <SimpleBar 
                              className="queries-responses-container"
                              style={{ maxHeight: '500px', overflowX: 'auto' }}
                              autoHide={false}
                            >
                              <h4 className="expanded-title">
                                {chatbot === 'openai' ? 'ChatGPT' : chatbot === 'gemini' ? 'Gemini' : chatbot === 'grok' ? 'Grok' : 'Perplexity'} - Queries and Responses
                              </h4>
                              <table className="queries-table">
                                <thead>
                                  <tr>
                                    <th className="query-column">User Query</th>
                                    <th className="response-column">AI Response</th>
                                    <th className="mentioned-column">Mentioned</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {analysisResults[chatbot].queryResponsePairs.map((pair, index) => (
                                    <tr key={index} className="query-row">
                                      <td className="query-cell">{pair.query}</td>
                                      <td className="response-cell">
                                        <SimpleBar 
                                          autoHide={false}
                                          style={{ maxHeight: '150px' }}
                                        >
                                          <div className="markdown-content">
                                            <ReactMarkdown>{pair.response}</ReactMarkdown>
                                          </div>
                                        </SimpleBar>
                                      </td>
                                      <td className="mentioned-cell">
                                        <span className={`status-badge ${pair.mentioned ? 'mentioned' : 'not-mentioned'}`}>
                                          {pair.mentioned ? 'Mentioned ✓' : 'Not mentioned ✗'}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </SimpleBar>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Business Ranking Row */}
                    <tr className="table-row">
                      <td className="metric-label">
                        <strong>Your Business Ranking</strong>
                        <div className="metric-description">How often is your business is mentioned, compared to competitors?</div>
                      </td>
                      <td className="metric-cell">
                        <div className="ranking-display">
                          {analysisResults.openai?.rankingScore?.rankPosition ? (
                            <>
                              <span className="ranking-number">#{analysisResults.openai.rankingScore.rankPosition}</span>
                              <span className="ranking-total">of {analysisResults.openai.rankingScore.totalCompetitors}</span>
                            </>
                          ) : (
                            <div className="not-ranked">
                              <span className="not-ranked-text">Not ranked 😔</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="ranking-display">
                          {analysisResults.gemini?.rankingScore?.rankPosition ? (
                            <>
                              <span className="ranking-number">#{analysisResults.gemini.rankingScore.rankPosition}</span>
                              <span className="ranking-total">of {analysisResults.gemini.rankingScore.totalCompetitors}</span>
                            </>
                          ) : (
                            <div className="not-ranked">
                              <span className="not-ranked-text">Not ranked 😔</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="ranking-display">
                          {analysisResults.grok?.rankingScore?.rankPosition ? (
                            <>
                              <span className="ranking-number">#{analysisResults.grok.rankingScore.rankPosition}</span>
                              <span className="ranking-total">of {analysisResults.grok.rankingScore.totalCompetitors}</span>
                            </>
                          ) : (
                            <div className="not-ranked">
                              <span className="not-ranked-text">Not ranked 😔</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="ranking-display">
                          {analysisResults.perplexity?.rankingScore?.rankPosition ? (
                            <>
                              <span className="ranking-number">#{analysisResults.perplexity.rankingScore.rankPosition}</span>
                              <span className="ranking-total">of {analysisResults.perplexity.rankingScore.totalCompetitors}</span>
                            </>
                          ) : (
                            <div className="not-ranked">
                              <span className="not-ranked-text">Not ranked 😔</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Top Competitors Row */}
                    <tr className="table-row">
                      <td className="metric-label">
                        <strong>Your top competitors</strong>
                        <div className="metric-description">Which businesses that are mentioned most often in AI chats?</div>
                      </td>
                      <td className="metric-cell">
                        <div className="competitors-list">
                          {analysisResults.openai?.frequencyRankings?.sortedCompetitors?.length > 0 ? (
                            analysisResults.openai.frequencyRankings.sortedCompetitors.slice(0, 10).map((competitor, index) => (
                              <div 
                                key={index} 
                                className={`competitor-item ${isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}
                              >
                                <span className="competitor-rank">#{index + 1}</span>
                                <span className="competitor-name">{competitor}</span>
                                {isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) && (
                                  <span className="your-business-badge">Your Business</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="no-data">No competitor data available</div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="competitors-list">
                          {analysisResults.gemini?.frequencyRankings?.sortedCompetitors?.length > 0 ? (
                            analysisResults.gemini.frequencyRankings.sortedCompetitors.slice(0, 10).map((competitor, index) => (
                              <div 
                                key={index} 
                                className={`competitor-item ${isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}
                              >
                                <span className="competitor-rank">#{index + 1}</span>
                                <span className="competitor-name">{competitor}</span>
                                {isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) && (
                                  <span className="your-business-badge">Your Business</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="no-data">No competitor data available</div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="competitors-list">
                          {analysisResults.grok?.frequencyRankings?.sortedCompetitors?.length > 0 ? (
                            analysisResults.grok.frequencyRankings.sortedCompetitors.slice(0, 10).map((competitor, index) => (
                              <div 
                                key={index} 
                                className={`competitor-item ${isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}
                              >
                                <span className="competitor-rank">#{index + 1}</span>
                                <span className="competitor-name">{competitor}</span>
                                {isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) && (
                                  <span className="your-business-badge">Your Business</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="no-data">No competitor data available</div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="competitors-list">
                          {analysisResults.perplexity?.frequencyRankings?.sortedCompetitors?.length > 0 ? (
                            analysisResults.perplexity.frequencyRankings.sortedCompetitors.slice(0, 10).map((competitor, index) => (
                              <div 
                                key={index} 
                                className={`competitor-item ${isFuzzyMatch(competitor, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}
                              >
                                <span className="competitor-rank">#{index + 1}</span>
                                <span className="competitor-name">{competitor}</span>
                                {isFuzzyMatch(competitor, businessData?.name || analysisResults.summary.cleanBusinessName) && (
                                  <span className="your-business-badge">Your Business</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="no-data">No competitor data available</div>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Customer Feedback Row */}
                    <tr className="table-row">
                      <td className="metric-label">
                        <strong>Top Themes</strong>
                        <div className="metric-description">What are the top themes that AI chats mention about your business?</div>
                      </td>
                      <td className="metric-cell">
                        <div className="sentiment-header">
                          <span className="sentiment-label">Sentiment Score:</span>
                          <div className="sentiment-score">
                            <span className={`score-fraction ${getSentimentClass(analysisResults.openai?.themes?.sentimentScore)}`}>
                              {analysisResults.openai?.themes?.sentimentScore ? `${analysisResults.openai.themes.sentimentScore}/100` : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <SimpleBar className="themes-list" style={{ maxHeight: '400px' }}>
                          {analysisResults.openai?.themes?.complaints ? (
                            <div className="markdown-content">
                              <ReactMarkdown components={markdownComponents}>{analysisResults.openai.themes.complaints}</ReactMarkdown>
                            </div>
                          ) : (
                            <div className="no-data">No customer feedback available</div>
                          )}
                        </SimpleBar>
                      </td>
                      <td className="metric-cell">
                        <div className="sentiment-header">
                          <span className="sentiment-label">Sentiment Score:</span>
                          <div className="sentiment-score">
                            <span className={`score-fraction ${getSentimentClass(analysisResults.gemini?.themes?.sentimentScore)}`}>
                              {analysisResults.gemini?.themes?.sentimentScore ? `${analysisResults.gemini.themes.sentimentScore}/100` : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <SimpleBar className="themes-list" style={{ maxHeight: '400px' }}>
                          {analysisResults.gemini?.themes?.complaints ? (
                            <div className="markdown-content">
                              <ReactMarkdown components={markdownComponents}>{analysisResults.gemini.themes.complaints}</ReactMarkdown>
                            </div>
                          ) : (
                            <div className="no-data">No customer feedback available</div>
                          )}
                        </SimpleBar>
                      </td>
                      <td className="metric-cell">
                        <div className="sentiment-header">
                          <span className="sentiment-label">Sentiment Score:</span>
                          <div className="sentiment-score">
                            <span className={`score-fraction ${getSentimentClass(analysisResults.grok?.themes?.sentimentScore)}`}>
                              {analysisResults.grok?.themes?.sentimentScore ? `${analysisResults.grok.themes.sentimentScore}/100` : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <SimpleBar className="themes-list" style={{ maxHeight: '400px' }}>
                          {analysisResults.grok?.themes?.complaints ? (
                            <div className="markdown-content">
                              <ReactMarkdown components={markdownComponents}>{analysisResults.grok.themes.complaints}</ReactMarkdown>
                            </div>
                          ) : (
                            <div className="no-data">No customer feedback available</div>
                          )}
                        </SimpleBar>
                      </td>
                      <td className="metric-cell">
                        <div className="sentiment-header">
                          <span className="sentiment-label">Sentiment Score:</span>
                          <div className="sentiment-score">
                            <span className={`score-fraction ${getSentimentClass(analysisResults.perplexity?.themes?.sentimentScore)}`}>
                              {analysisResults.perplexity?.themes?.sentimentScore ? `${analysisResults.perplexity.themes.sentimentScore}/100` : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <SimpleBar className="themes-list" style={{ maxHeight: '400px' }}>
                          {analysisResults.perplexity?.themes?.complaints ? (
                            <div className="markdown-content">
                              <ReactMarkdown components={markdownComponents}>{analysisResults.perplexity.themes.complaints}</ReactMarkdown>
                            </div>
                          ) : (
                            <div className="no-data">No customer feedback available</div>
                          )}
                        </SimpleBar>
                      </td>
                    </tr>

                    {/* Top Sources Row */}
                    <tr className="table-row">
                      <td className="metric-label">
                        <strong>Top Sources</strong>
                        <div className="metric-description">Where are AI chatbots finding their information?</div>
                      </td>
                      <td className="metric-cell">
                        <div className="citation-sources-list">
                          {analysisResults.openai?.topCitationSources?.length > 0 ? (
                            analysisResults.openai.topCitationSources.map((source, index) => {
                              const isExpanded = expandedCitationDomains.openai === source.domain;
                              const urls = isExpanded ? getUrlsForDomain(analysisResults.openai.queryResponsePairs, source.domain) : [];
                              
                              return (
                                <div key={index} className="citation-item-wrapper">
                                  <div 
                                    className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                    onClick={() => toggleCitationDomainExpansion('openai', source.domain)}
                                    title="Click to see full URLs"
                                  >
                                    <span className="citation-rank">#{index + 1}</span>
                                    <span className="citation-domain">{source.domain}</span>
                                    <span className="citation-count">({source.count})</span>
                                    <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                  </div>
                                  {isExpanded && urls.length > 0 && (
                                    <div className="citation-urls-list">
                                      {(() => {
                                        const showAllKey = `openai-${source.domain}`;
                                        const showAll = showAllUrls[showAllKey];
                                        const maxUrlsToShow = 5;
                                        const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                        const hasMore = urls.length > maxUrlsToShow;
                                        
                                        return (
                                          <>
                                            <div className="urls-container">
                                              {urlsToDisplay.map((urlData, urlIndex) => (
                                                <a 
                                                  key={urlIndex} 
                                                  href={urlData.url} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer" 
                                                  className="citation-url"
                                                  title={urlData.url}
                                                  onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                                >
                                                  <span className="url-index">{urlIndex + 1}.</span>
                                                  <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                                  <span className="url-citation-count">({urlData.count}x)</span>
                                                </a>
                                              ))}
                                            </div>
                                            {hasMore && (
                                              <button 
                                                className="show-more-urls-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleShowAllUrls('openai', source.domain);
                                                }}
                                              >
                                                {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                              </button>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="no-data">No citation data available</div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="citation-sources-list">
                          {analysisResults.gemini?.topCitationSources?.length > 0 ? (
                            analysisResults.gemini.topCitationSources.map((source, index) => {
                              const isExpanded = expandedCitationDomains.gemini === source.domain;
                              const urls = isExpanded ? getUrlsForDomain(analysisResults.gemini.queryResponsePairs, source.domain) : [];
                              
                              return (
                                <div key={index} className="citation-item-wrapper">
                                  <div 
                                    className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                    onClick={() => toggleCitationDomainExpansion('gemini', source.domain)}
                                    title="Click to see full URLs"
                                  >
                                    <span className="citation-rank">#{index + 1}</span>
                                    <span className="citation-domain">{source.domain}</span>
                                    <span className="citation-count">({source.count})</span>
                                    <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                  </div>
                                  {isExpanded && urls.length > 0 && (
                                    <div className="citation-urls-list">
                                      {(() => {
                                        // For Gemini, show same layout as others but use domain-based URL
                                        const showAllKey = `gemini-${source.domain}`;
                                        const showAll = showAllUrls[showAllKey];
                                        const maxUrlsToShow = 5;
                                        const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                        const hasMore = urls.length > maxUrlsToShow;
                                        const domainName = formatUrlForDisplay(`https://${source.domain}`);
                                        
                                        return (
                                          <>
                                            <div className="urls-container">
                                              {urlsToDisplay.map((urlData, urlIndex) => (
                                                <a 
                                                  key={urlIndex} 
                                                  href={urlData.url} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer" 
                                                  className="citation-url"
                                                  title={urlData.url}
                                                >
                                                  <span className="url-index">{urlIndex + 1}.</span>
                                                  <span className="url-text">{domainName}</span>
                                                  <span className="url-citation-count">({urlData.count}x)</span>
                                                </a>
                                              ))}
                                            </div>
                                            {hasMore && (
                                              <button 
                                                className="show-more-urls-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleShowAllUrls('gemini', source.domain);
                                                }}
                                              >
                                                {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                              </button>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="no-data">No citation data available</div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="citation-sources-list">
                          {analysisResults.grok?.topCitationSources?.length > 0 ? (
                            analysisResults.grok.topCitationSources.map((source, index) => {
                              const isExpanded = expandedCitationDomains.grok === source.domain;
                              const urls = isExpanded ? getUrlsForDomain(analysisResults.grok.queryResponsePairs, source.domain) : [];
                              
                              return (
                                <div key={index} className="citation-item-wrapper">
                                  <div 
                                    className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                    onClick={() => toggleCitationDomainExpansion('grok', source.domain)}
                                    title="Click to see full URLs"
                                  >
                                    <span className="citation-rank">#{index + 1}</span>
                                    <span className="citation-domain">{source.domain}</span>
                                    <span className="citation-count">({source.count})</span>
                                    <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                  </div>
                                  {isExpanded && urls.length > 0 && (
                                    <div className="citation-urls-list">
                                      {(() => {
                                        const showAllKey = `grok-${source.domain}`;
                                        const showAll = showAllUrls[showAllKey];
                                        const maxUrlsToShow = 5;
                                        const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                        const hasMore = urls.length > maxUrlsToShow;
                                        
                                        return (
                                          <>
                                            <div className="urls-container">
                                              {urlsToDisplay.map((urlData, urlIndex) => (
                                                <a 
                                                  key={urlIndex} 
                                                  href={urlData.url} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer" 
                                                  className="citation-url"
                                                  title={urlData.url}
                                                  onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                                >
                                                  <span className="url-index">{urlIndex + 1}.</span>
                                                  <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                                  <span className="url-citation-count">({urlData.count}x)</span>
                                                </a>
                                              ))}
                                            </div>
                                            {hasMore && (
                                              <button 
                                                className="show-more-urls-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleShowAllUrls('grok', source.domain);
                                                }}
                                              >
                                                {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                              </button>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="no-data">No citation data available</div>
                          )}
                        </div>
                      </td>
                      <td className="metric-cell">
                        <div className="citation-sources-list">
                          {analysisResults.perplexity?.topCitationSources?.length > 0 ? (
                            analysisResults.perplexity.topCitationSources.map((source, index) => {
                              const isExpanded = expandedCitationDomains.perplexity === source.domain;
                              const urls = isExpanded ? getUrlsForDomain(analysisResults.perplexity.queryResponsePairs, source.domain) : [];
                              
                              // Debug log for Perplexity
                              if (isExpanded) {
                                console.log('Perplexity - Expanded domain:', source.domain);
                                console.log('Perplexity - Query Response Pairs:', analysisResults.perplexity.queryResponsePairs);
                                console.log('Perplexity - URLs found:', urls);
                              }
                              
                              return (
                                <div key={index} className="citation-item-wrapper">
                                  <div 
                                    className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                    onClick={() => toggleCitationDomainExpansion('perplexity', source.domain)}
                                    title="Click to see full URLs"
                                  >
                                    <span className="citation-rank">#{index + 1}</span>
                                    <span className="citation-domain">{source.domain}</span>
                                    <span className="citation-count">({source.count})</span>
                                    <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                  </div>
                                  {isExpanded && urls.length > 0 && (
                                    <div className="citation-urls-list">
                                      {(() => {
                                        const showAllKey = `perplexity-${source.domain}`;
                                        const showAll = showAllUrls[showAllKey];
                                        const maxUrlsToShow = 5;
                                        const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                        const hasMore = urls.length > maxUrlsToShow;
                                        
                                        return (
                                          <>
                                            <div className="urls-container">
                                              {urlsToDisplay.map((urlData, urlIndex) => (
                                                <a 
                                                  key={urlIndex} 
                                                  href={urlData.url} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer" 
                                                  className="citation-url"
                                                  title={urlData.url}
                                                  onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                                >
                                                  <span className="url-index">{urlIndex + 1}.</span>
                                                  <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                                  <span className="url-citation-count">({urlData.count}x)</span>
                                                </a>
                                              ))}
                                            </div>
                                            {hasMore && (
                                              <button 
                                                className="show-more-urls-btn"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleShowAllUrls('perplexity', source.domain);
                                                }}
                                              >
                                                {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                              </button>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="no-data">No citation data available</div>
                          )}
                        </div>
                      </td>
                    </tr>

                  </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="analysis-cards-container mobile-view">
                {/* Visibility Score Card */}
                <div className="metric-card">
                  <h3 className="card-title">Visibility Score</h3>
                  <p className="card-description">What percentage of AI chats mention your business?</p>
                  
                  <div className="chatbot-scores">
                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" className="chatbot-icon-small" />
                        <span>ChatGPT</span>
                      </div>
                      <div className="score-display">
                        <div className="score-bar-mobile">
                          <div 
                            className="score-fill"
                            style={{ width: `${analysisResults.openai?.visibilityScore?.percentage || 0}%` }}
                          />
                        </div>
                        <span className="score-text-mobile">{analysisResults.openai?.visibilityScore?.percentage || 0}%</span>
                      </div>
                      <button 
                        className="view-queries-btn"
                        onClick={() => toggleVisibilityScoreExpansion('openai')}
                      >
                        {expandedVisibilityScores.openai ? 'Hide' : 'View'} chats and responses
                        <span className={`mobile-expansion-icon ${expandedVisibilityScores.openai ? 'expanded' : ''}`}>▶</span>
                      </button>
                    </div>
                    
                    {/* Expanded queries for OpenAI mobile */}
                    {expandedVisibilityScores.openai && analysisResults.openai?.queryResponsePairs && (
                      <div className="mobile-expanded-queries">
                        <SimpleBar 
                          className="queries-responses-container-mobile"
                          style={{ maxHeight: '400px' }}
                          autoHide={false}
                        >
                          <div className="expanded-title">
                            <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
                            ChatGPT - Queries and Responses
                          </div>
                          {analysisResults.openai.queryResponsePairs.map((pair, index) => {
                            const isExpanded = expandedResponses.openai[index];
                            return (
                              <div key={index} className="query-item-mobile">
                                <div className="query-header-mobile">
                                  <span className="query-number">Chat {index + 1}</span>
                                  <span className={`status-badge ${pair.mentioned ? 'mentioned' : 'not-mentioned'}`}>
                                    {pair.mentioned ? 'Mentioned ✓' : 'Not mentioned ✗'}
                                  </span>
                                </div>
                                <div className="query-text-detail">{pair.query}</div>
                                <div className="response-header-mobile">Response:</div>
                                <div className={`response-section ${isExpanded ? '' : 'response-collapsed'}`}>
                                  <div className="markdown-content">
                                    <ReactMarkdown>{pair.response}</ReactMarkdown>
                                  </div>
                                </div>
                                {!isExpanded && (
                                  <button
                                    className="expand-response-btn"
                                    onClick={() => {
                                      setExpandedResponses(prev => ({
                                        ...prev,
                                        openai: { ...prev.openai, [index]: true }
                                      }));
                                    }}
                                  >
                                    See full response
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </SimpleBar>
                      </div>
                    )}

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GeminiIcon.svg" alt="Gemini" className="chatbot-icon-small" />
                        <span>Gemini</span>
                      </div>
                      <div className="score-display">
                        <div className="score-bar-mobile">
                          <div 
                            className="score-fill"
                            style={{ width: `${analysisResults.gemini?.visibilityScore?.percentage || 0}%` }}
                          />
                        </div>
                        <span className="score-text-mobile">{analysisResults.gemini?.visibilityScore?.percentage || 0}%</span>
                      </div>
                      <button 
                        className="view-queries-btn"
                        onClick={() => toggleVisibilityScoreExpansion('gemini')}
                      >
                        {expandedVisibilityScores.gemini ? 'Hide' : 'View'} chats and responses
                        <span className={`mobile-expansion-icon ${expandedVisibilityScores.gemini ? 'expanded' : ''}`}>▶</span>
                      </button>
                    </div>
                    
                    {/* Expanded queries for Gemini mobile */}
                    {expandedVisibilityScores.gemini && analysisResults.gemini?.queryResponsePairs && (
                      <div className="mobile-expanded-queries">
                        <SimpleBar 
                          className="queries-responses-container-mobile"
                          style={{ maxHeight: '400px' }}
                          autoHide={false}
                        >
                          <div className="expanded-title">
                            <img src="/images/GeminiIcon.svg" alt="Gemini" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
                            Gemini - Queries and Responses
                          </div>
                          {analysisResults.gemini.queryResponsePairs.map((pair, index) => {
                            const isExpanded = expandedResponses.gemini[index];
                            return (
                              <div key={index} className="query-item-mobile">
                                <div className="query-header-mobile">
                                  <span className="query-number">Chat {index + 1}</span>
                                  <span className={`status-badge ${pair.mentioned ? 'mentioned' : 'not-mentioned'}`}>
                                    {pair.mentioned ? 'Mentioned ✓' : 'Not mentioned ✗'}
                                  </span>
                                </div>
                                <div className="query-text-detail">{pair.query}</div>
                                <div className="response-header-mobile">Response:</div>
                                <div className={`response-section ${isExpanded ? '' : 'response-collapsed'}`}>
                                  <div className="markdown-content">
                                    <ReactMarkdown>{pair.response}</ReactMarkdown>
                                  </div>
                                </div>
                                {!isExpanded && (
                                  <button
                                    className="expand-response-btn"
                                    onClick={() => {
                                      setExpandedResponses(prev => ({
                                        ...prev,
                                        gemini: { ...prev.gemini, [index]: true }
                                      }));
                                    }}
                                  >
                                    See full response
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </SimpleBar>
                      </div>
                    )}

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GrokIcon.png" alt="Grok" className="chatbot-icon-small" />
                        <span>Grok</span>
                      </div>
                      <div className="score-display">
                        <div className="score-bar-mobile">
                          <div 
                            className="score-fill"
                            style={{ width: `${analysisResults.grok?.visibilityScore?.percentage || 0}%` }}
                          />
                        </div>
                        <span className="score-text-mobile">{analysisResults.grok?.visibilityScore?.percentage || 0}%</span>
                      </div>
                      <button 
                        className="view-queries-btn"
                        onClick={() => toggleVisibilityScoreExpansion('grok')}
                      >
                        {expandedVisibilityScores.grok ? 'Hide' : 'View'} chats and responses
                        <span className={`mobile-expansion-icon ${expandedVisibilityScores.grok ? 'expanded' : ''}`}>▶</span>
                      </button>
                    </div>
                    
                    {/* Expanded queries for Grok mobile */}
                    {expandedVisibilityScores.grok && analysisResults.grok?.queryResponsePairs && (
                      <div className="mobile-expanded-queries">
                        <SimpleBar 
                          className="queries-responses-container-mobile"
                          style={{ maxHeight: '400px' }}
                          autoHide={false}
                        >
                          <div className="expanded-title">
                            <img src="/images/GrokIcon.png" alt="Grok" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
                            Grok - Queries and Responses
                          </div>
                          {analysisResults.grok.queryResponsePairs.map((pair, index) => {
                            const isExpanded = expandedResponses.grok[index];
                            return (
                              <div key={index} className="query-item-mobile">
                                <div className="query-header-mobile">
                                  <span className="query-number">Chat {index + 1}</span>
                                  <span className={`status-badge ${pair.mentioned ? 'mentioned' : 'not-mentioned'}`}>
                                    {pair.mentioned ? 'Mentioned ✓' : 'Not mentioned ✗'}
                                  </span>
                                </div>
                                <div className="query-text-detail">{pair.query}</div>
                                <div className="response-header-mobile">Response:</div>
                                <div className={`response-section ${isExpanded ? '' : 'response-collapsed'}`}>
                                  <div className="markdown-content">
                                    <ReactMarkdown>{pair.response}</ReactMarkdown>
                                  </div>
                                </div>
                                {!isExpanded && (
                                  <button
                                    className="expand-response-btn"
                                    onClick={() => {
                                      setExpandedResponses(prev => ({
                                        ...prev,
                                        grok: { ...prev.grok, [index]: true }
                                      }));
                                    }}
                                  >
                                    See full response
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </SimpleBar>
                      </div>
                    )}

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/PerplexityIcon.svg" alt="Perplexity" className="chatbot-icon-small" />
                        <span>Perplexity</span>
                      </div>
                      <div className="score-display">
                        <div className="score-bar-mobile">
                          <div 
                            className="score-fill"
                            style={{ width: `${analysisResults.perplexity?.visibilityScore?.percentage || 0}%` }}
                          />
                        </div>
                        <span className="score-text-mobile">{analysisResults.perplexity?.visibilityScore?.percentage || 0}%</span>
                      </div>
                      <button 
                        className="view-queries-btn"
                        onClick={() => toggleVisibilityScoreExpansion('perplexity')}
                      >
                        {expandedVisibilityScores.perplexity ? 'Hide' : 'View'} chats and responses
                        <span className={`mobile-expansion-icon ${expandedVisibilityScores.perplexity ? 'expanded' : ''}`}>▶</span>
                      </button>
                    </div>
                    
                    {/* Expanded queries for Perplexity mobile */}
                    {expandedVisibilityScores.perplexity && analysisResults.perplexity?.queryResponsePairs && (
                      <div className="mobile-expanded-queries">
                        <SimpleBar 
                          className="queries-responses-container-mobile"
                          style={{ maxHeight: '400px' }}
                          autoHide={false}
                        >
                          <div className="expanded-title">
                            <img src="/images/PerplexityIcon.svg" alt="Perplexity" style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }} />
                            Perplexity - Queries and Responses
                          </div>
                          {analysisResults.perplexity.queryResponsePairs.map((pair, index) => {
                            const isExpanded = expandedResponses.perplexity[index];
                            return (
                              <div key={index} className="query-item-mobile">
                                <div className="query-header-mobile">
                                  <span className="query-number">Chat {index + 1}</span>
                                  <span className={`status-badge ${pair.mentioned ? 'mentioned' : 'not-mentioned'}`}>
                                    {pair.mentioned ? 'Mentioned ✓' : 'Not mentioned ✗'}
                                  </span>
                                </div>
                                <div className="query-text-detail">{pair.query}</div>
                                <div className="response-header-mobile">Response:</div>
                                <div className={`response-section ${isExpanded ? '' : 'response-collapsed'}`}>
                                  <div className="markdown-content">
                                    <ReactMarkdown>{pair.response}</ReactMarkdown>
                                  </div>
                                </div>
                                {!isExpanded && (
                                  <button
                                    className="expand-response-btn"
                                    onClick={() => {
                                      setExpandedResponses(prev => ({
                                        ...prev,
                                        perplexity: { ...prev.perplexity, [index]: true }
                                      }));
                                    }}
                                  >
                                    See full response
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </SimpleBar>
                      </div>
                    )}
                  </div>
                </div>

                {/* Business Ranking Card */}
                <div className="metric-card">
                  <h3 className="card-title">Your Business Ranking</h3>
                  <p className="card-description">How often is your business mentioned, compared to competitors?</p>
                  
                  <div className="chatbot-scores">
                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" className="chatbot-icon-small" />
                        <span>ChatGPT</span>
                      </div>
                      <div className="ranking-display-mobile">
                        {analysisResults.openai?.rankingScore?.rankPosition ? (
                          <>
                            <span className="ranking-number">#{analysisResults.openai.rankingScore.rankPosition}</span>
                            <span className="ranking-total">of {analysisResults.openai.rankingScore.totalCompetitors}</span>
                          </>
                        ) : (
                          <span className="not-ranked-text">Not ranked 😔</span>
                        )}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GeminiIcon.svg" alt="Gemini" className="chatbot-icon-small" />
                        <span>Gemini</span>
                      </div>
                      <div className="ranking-display-mobile">
                        {analysisResults.gemini?.rankingScore?.rankPosition ? (
                          <>
                            <span className="ranking-number">#{analysisResults.gemini.rankingScore.rankPosition}</span>
                            <span className="ranking-total">of {analysisResults.gemini.rankingScore.totalCompetitors}</span>
                          </>
                        ) : (
                          <span className="not-ranked-text">Not ranked 😔</span>
                        )}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GrokIcon.png" alt="Grok" className="chatbot-icon-small" />
                        <span>Grok</span>
                      </div>
                      <div className="ranking-display-mobile">
                        {analysisResults.grok?.rankingScore?.rankPosition ? (
                          <>
                            <span className="ranking-number">#{analysisResults.grok.rankingScore.rankPosition}</span>
                            <span className="ranking-total">of {analysisResults.grok.rankingScore.totalCompetitors}</span>
                          </>
                        ) : (
                          <span className="not-ranked-text">Not ranked 😔</span>
                        )}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/PerplexityIcon.svg" alt="Perplexity" className="chatbot-icon-small" />
                        <span>Perplexity</span>
                      </div>
                      <div className="ranking-display-mobile">
                        {analysisResults.perplexity?.rankingScore?.rankPosition ? (
                          <>
                            <span className="ranking-number">#{analysisResults.perplexity.rankingScore.rankPosition}</span>
                            <span className="ranking-total">of {analysisResults.perplexity.rankingScore.totalCompetitors}</span>
                          </>
                        ) : (
                          <span className="not-ranked-text">Not ranked 😔</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Top Competitors Card */}
                <div className="metric-card">
                  <h3 className="card-title">Top Competitors</h3>
                  <p className="card-description">Businesses mentioned most frequently across all AI chatbots</p>
                  
                  <div className="chatbot-scores">
                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" className="chatbot-icon-small" />
                        <span>ChatGPT</span>
                      </div>
                      <div className="competitors-list-mobile">
                        {analysisResults.openai?.frequencyRankings?.sortedCompetitors?.slice(0, 10).map((comp, idx) => (
                          <div key={idx} className={`competitor-item ${isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}>
                            <span className="competitor-rank">#{idx + 1}</span>
                            <span className="competitor-name">{comp}</span>
                            {isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) && (
                              <span className="your-business-badge">Your Business</span>
                            )}
                          </div>
                        )) || <span className="no-data">No competitors found</span>}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GeminiIcon.svg" alt="Gemini" className="chatbot-icon-small" />
                        <span>Gemini</span>
                      </div>
                      <div className="competitors-list-mobile">
                        {analysisResults.gemini?.frequencyRankings?.sortedCompetitors?.slice(0, 10).map((comp, idx) => (
                          <div key={idx} className={`competitor-item ${isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}>
                            <span className="competitor-rank">#{idx + 1}</span>
                            <span className="competitor-name">{comp}</span>
                            {isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) && (
                              <span className="your-business-badge">Your Business</span>
                            )}
                          </div>
                        )) || <span className="no-data">No competitors found</span>}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GrokIcon.png" alt="Grok" className="chatbot-icon-small" />
                        <span>Grok</span>
                      </div>
                      <div className="competitors-list-mobile">
                        {analysisResults.grok?.frequencyRankings?.sortedCompetitors?.slice(0, 10).map((comp, idx) => (
                          <div key={idx} className={`competitor-item ${isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}>
                            <span className="competitor-rank">#{idx + 1}</span>
                            <span className="competitor-name">{comp}</span>
                            {isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) && (
                              <span className="your-business-badge">Your Business</span>
                            )}
                          </div>
                        )) || <span className="no-data">No competitors found</span>}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/PerplexityIcon.svg" alt="Perplexity" className="chatbot-icon-small" />
                        <span>Perplexity</span>
                      </div>
                      <div className="competitors-list-mobile">
                        {analysisResults.perplexity?.frequencyRankings?.sortedCompetitors?.slice(0, 10).map((comp, idx) => (
                          <div key={idx} className={`competitor-item ${isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) ? 'highlighted' : ''}`}>
                            <span className="competitor-rank">#{idx + 1}</span>
                            <span className="competitor-name">{comp}</span>
                            {isFuzzyMatch(comp, analysisResults.summary.cleanBusinessName) && (
                              <span className="your-business-badge">Your Business</span>
                            )}
                          </div>
                        )) || <span className="no-data">No competitors found</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI Chat Sentiment Card */}
                <div className="metric-card">
                  <h3 className="card-title">Top Themes</h3>
                  <p className="card-description">What is the tone when your business is mentioned?</p>
                  
                  <div className="chatbot-scores">
                    <div className="chatbot-score-item themes-item">
                      <div className="themes-header-mobile">
                        <div className="chatbot-label">
                          <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" className="chatbot-icon-small" />
                          <span>ChatGPT</span>
                        </div>
                        <div className="sentiment-score-right">
                          {analysisResults.openai?.themes?.sentimentScore ? (
                            <>
                              <span className="sentiment-label-small">Sentiment:</span>
                              <span className={`score-fraction ${getSentimentClass(analysisResults.openai.themes.sentimentScore)}`}>
                                {analysisResults.openai.themes.sentimentScore}/100
                              </span>
                            </>
                          ) : (
                            <span className="no-data">N/A</span>
                          )}
                        </div>
                      </div>
                      {analysisResults.openai?.themes?.complaints && (
                        <div className="themes-content-mobile" style={{ fontSize: '13px', lineHeight: '1.4', marginTop: '10px' }}>
                          <SimpleBar style={{ maxHeight: '200px' }} autoHide={false}>
                            <div className="markdown-content">
                              <ReactMarkdown>{analysisResults.openai.themes.complaints}</ReactMarkdown>
                            </div>
                          </SimpleBar>
                        </div>
                      )}
                    </div>

                    <div className="chatbot-score-item themes-item">
                      <div className="themes-header-mobile">
                        <div className="chatbot-label">
                          <img src="/images/GeminiIcon.svg" alt="Gemini" className="chatbot-icon-small" />
                          <span>Gemini</span>
                        </div>
                        <div className="sentiment-score-right">
                          {analysisResults.gemini?.themes?.sentimentScore ? (
                            <>
                              <span className="sentiment-label-small">Sentiment:</span>
                              <span className={`score-fraction ${getSentimentClass(analysisResults.gemini.themes.sentimentScore)}`}>
                                {analysisResults.gemini.themes.sentimentScore}/100
                              </span>
                            </>
                          ) : (
                            <span className="no-data">N/A</span>
                          )}
                        </div>
                      </div>
                      {analysisResults.gemini?.themes?.complaints && (
                        <div className="themes-content-mobile" style={{ fontSize: '13px', lineHeight: '1.4', marginTop: '10px' }}>
                          <SimpleBar style={{ maxHeight: '200px' }} autoHide={false}>
                            <div className="markdown-content">
                              <ReactMarkdown>{analysisResults.gemini.themes.complaints}</ReactMarkdown>
                            </div>
                          </SimpleBar>
                        </div>
                      )}
                    </div>

                    <div className="chatbot-score-item themes-item">
                      <div className="themes-header-mobile">
                        <div className="chatbot-label">
                          <img src="/images/GrokIcon.png" alt="Grok" className="chatbot-icon-small" />
                          <span>Grok</span>
                        </div>
                        <div className="sentiment-score-right">
                          {analysisResults.grok?.themes?.sentimentScore ? (
                            <>
                              <span className="sentiment-label-small">Sentiment:</span>
                              <span className={`score-fraction ${getSentimentClass(analysisResults.grok.themes.sentimentScore)}`}>
                                {analysisResults.grok.themes.sentimentScore}/100
                              </span>
                            </>
                          ) : (
                            <span className="no-data">N/A</span>
                          )}
                        </div>
                      </div>
                      {analysisResults.grok?.themes?.complaints && (
                        <div className="themes-content-mobile" style={{ fontSize: '13px', lineHeight: '1.4', marginTop: '10px' }}>
                          <SimpleBar style={{ maxHeight: '200px' }} autoHide={false}>
                            <div className="markdown-content">
                              <ReactMarkdown>{analysisResults.grok.themes.complaints}</ReactMarkdown>
                            </div>
                          </SimpleBar>
                        </div>
                      )}
                    </div>

                    <div className="chatbot-score-item themes-item">
                      <div className="themes-header-mobile">
                        <div className="chatbot-label">
                          <img src="/images/PerplexityIcon.svg" alt="Perplexity" className="chatbot-icon-small" />
                          <span>Perplexity</span>
                        </div>
                        <div className="sentiment-score-right">
                          {analysisResults.perplexity?.themes?.sentimentScore ? (
                            <>
                              <span className="sentiment-label-small">Sentiment:</span>
                              <span className={`score-fraction ${getSentimentClass(analysisResults.perplexity.themes.sentimentScore)}`}>
                                {analysisResults.perplexity.themes.sentimentScore}/100
                              </span>
                            </>
                          ) : (
                            <span className="no-data">N/A</span>
                          )}
                        </div>
                      </div>
                      {analysisResults.perplexity?.themes?.complaints && (
                        <div className="themes-content-mobile" style={{ fontSize: '13px', lineHeight: '1.4', marginTop: '10px' }}>
                          <SimpleBar style={{ maxHeight: '200px' }} autoHide={false}>
                            <div className="markdown-content">
                              <ReactMarkdown>{analysisResults.perplexity.themes.complaints}</ReactMarkdown>
                            </div>
                          </SimpleBar>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Top Citation Sources Card */}
                <div className="metric-card">
                  <h3 className="card-title">Top Sources</h3>
                  <p className="card-description">Where are AI chatbots finding their information?</p>
                  
                  <div className="chatbot-scores">
                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/ChatGPTIcon.svg" alt="ChatGPT" className="chatbot-icon-small" />
                        <span>ChatGPT</span>
                      </div>
                      <div className="citation-list-mobile">
                        {analysisResults.openai?.topCitationSources?.slice(0, 10).map((source, idx) => {
                          const isExpanded = expandedCitationDomains.openai === source.domain;
                          const urls = isExpanded ? getUrlsForDomain(analysisResults.openai.queryResponsePairs, source.domain) : [];
                          
                          return (
                            <div key={idx} className="citation-item-wrapper">
                              <div 
                                className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                onClick={() => toggleCitationDomainExpansion('openai', source.domain)}
                                title="Click to see full URLs"
                              >
                                <span className="citation-rank">#{idx + 1}</span>
                                <span className="citation-domain">{source.domain}</span>
                                <span className="citation-count">({source.count})</span>
                                <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                              </div>
                              {isExpanded && urls.length > 0 && (
                                <div className="citation-urls-list">
                                  {(() => {
                                    const showAllKey = `openai-${source.domain}`;
                                    const showAll = showAllUrls[showAllKey];
                                    const maxUrlsToShow = 5;
                                    const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                    const hasMore = urls.length > maxUrlsToShow;
                                    
                                    return (
                                      <>
                                        <div className="urls-container">
                                          {urlsToDisplay.map((urlData, urlIndex) => (
                                            <a 
                                              key={urlIndex} 
                                              href={urlData.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="citation-url"
                                              title={urlData.url}
                                              onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                            >
                                              <span className="url-index">{urlIndex + 1}.</span>
                                              <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                              <span className="url-citation-count">({urlData.count}x)</span>
                                            </a>
                                          ))}
                                        </div>
                                        {hasMore && (
                                          <button 
                                            className="show-more-urls-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleShowAllUrls('openai', source.domain);
                                            }}
                                          >
                                            {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        }) || <span className="no-data">No citations found</span>}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GeminiIcon.svg" alt="Gemini" className="chatbot-icon-small" />
                        <span>Gemini</span>
                      </div>
                      <div className="citation-list-mobile">
                        {analysisResults.gemini?.topCitationSources?.slice(0, 10).map((source, idx) => {
                          const isExpanded = expandedCitationDomains.gemini === source.domain;
                          const urls = isExpanded ? getUrlsForDomain(analysisResults.gemini.queryResponsePairs, source.domain) : [];
                          
                          return (
                            <div key={idx} className="citation-item-wrapper">
                              <div 
                                className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                onClick={() => toggleCitationDomainExpansion('gemini', source.domain)}
                                title="Click to see full URLs"
                              >
                                <span className="citation-rank">#{idx + 1}</span>
                                <span className="citation-domain">{source.domain}</span>
                                <span className="citation-count">({source.count})</span>
                                <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                              </div>
                              {isExpanded && urls.length > 0 && (
                                <div className="citation-urls-list">
                                  {(() => {
                                    const showAllKey = `gemini-${source.domain}`;
                                    const showAll = showAllUrls[showAllKey];
                                    const maxUrlsToShow = 5;
                                    const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                    const hasMore = urls.length > maxUrlsToShow;
                                    
                                    return (
                                      <>
                                        <div className="urls-container">
                                          {urlsToDisplay.map((urlData, urlIndex) => (
                                            <a 
                                              key={urlIndex} 
                                              href={urlData.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="citation-url"
                                              title={urlData.url}
                                              onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                            >
                                              <span className="url-index">{urlIndex + 1}.</span>
                                              <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                              <span className="url-citation-count">({urlData.count}x)</span>
                                            </a>
                                          ))}
                                        </div>
                                        {hasMore && (
                                          <button 
                                            className="show-more-urls-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleShowAllUrls('gemini', source.domain);
                                            }}
                                          >
                                            {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        }) || <span className="no-data">No citations found</span>}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/GrokIcon.png" alt="Grok" className="chatbot-icon-small" />
                        <span>Grok</span>
                      </div>
                      <div className="citation-list-mobile">
                        {analysisResults.grok?.topCitationSources?.slice(0, 10).map((source, idx) => {
                          const isExpanded = expandedCitationDomains.grok === source.domain;
                          const urls = isExpanded ? getUrlsForDomain(analysisResults.grok.queryResponsePairs, source.domain) : [];
                          
                          return (
                            <div key={idx} className="citation-item-wrapper">
                              <div 
                                className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                onClick={() => toggleCitationDomainExpansion('grok', source.domain)}
                                title="Click to see full URLs"
                              >
                                <span className="citation-rank">#{idx + 1}</span>
                                <span className="citation-domain">{source.domain}</span>
                                <span className="citation-count">({source.count})</span>
                                <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                              </div>
                              {isExpanded && urls.length > 0 && (
                                <div className="citation-urls-list">
                                  {(() => {
                                    const showAllKey = `grok-${source.domain}`;
                                    const showAll = showAllUrls[showAllKey];
                                    const maxUrlsToShow = 5;
                                    const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                    const hasMore = urls.length > maxUrlsToShow;
                                    
                                    return (
                                      <>
                                        <div className="urls-container">
                                          {urlsToDisplay.map((urlData, urlIndex) => (
                                            <a 
                                              key={urlIndex} 
                                              href={urlData.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="citation-url"
                                              title={urlData.url}
                                              onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                            >
                                              <span className="url-index">{urlIndex + 1}.</span>
                                              <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                              <span className="url-citation-count">({urlData.count}x)</span>
                                            </a>
                                          ))}
                                        </div>
                                        {hasMore && (
                                          <button 
                                            className="show-more-urls-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleShowAllUrls('grok', source.domain);
                                            }}
                                          >
                                            {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        }) || <span className="no-data">No citations found</span>}
                      </div>
                    </div>

                    <div className="chatbot-score-item">
                      <div className="chatbot-label">
                        <img src="/images/PerplexityIcon.svg" alt="Perplexity" className="chatbot-icon-small" />
                        <span>Perplexity</span>
                      </div>
                      <div className="citation-list-mobile">
                        {analysisResults.perplexity?.topCitationSources?.slice(0, 10).map((source, idx) => {
                          const isExpanded = expandedCitationDomains.perplexity === source.domain;
                          const urls = isExpanded ? getUrlsForDomain(analysisResults.perplexity.queryResponsePairs, source.domain) : [];
                          
                          return (
                            <div key={idx} className="citation-item-wrapper">
                              <div 
                                className={`citation-item ${isExpanded ? 'expanded' : ''} clickable`}
                                onClick={() => toggleCitationDomainExpansion('perplexity', source.domain)}
                                title="Click to see full URLs"
                              >
                                <span className="citation-rank">#{idx + 1}</span>
                                <span className="citation-domain">{source.domain}</span>
                                <span className="citation-count">({source.count})</span>
                                <span className="citation-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                              </div>
                              {isExpanded && urls.length > 0 && (
                                <div className="citation-urls-list">
                                  {(() => {
                                    const showAllKey = `perplexity-${source.domain}`;
                                    const showAll = showAllUrls[showAllKey];
                                    const maxUrlsToShow = 5;
                                    const urlsToDisplay = showAll ? urls : urls.slice(0, maxUrlsToShow);
                                    const hasMore = urls.length > maxUrlsToShow;
                                    
                                    return (
                                      <>
                                        <div className="urls-container">
                                          {urlsToDisplay.map((urlData, urlIndex) => (
                                            <a 
                                              key={urlIndex} 
                                              href={urlData.url} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="citation-url"
                                              title={urlData.url}
                                              onClick={(e) => handleCitationClick(e, urlData.url, 'citation-sources')}
                                            >
                                              <span className="url-index">{urlIndex + 1}.</span>
                                              <span className="url-text">{formatUrlForDisplay(urlData.url)}</span>
                                              <span className="url-citation-count">({urlData.count}x)</span>
                                            </a>
                                          ))}
                                        </div>
                                        {hasMore && (
                                          <button 
                                            className="show-more-urls-btn"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleShowAllUrls('perplexity', source.domain);
                                            }}
                                          >
                                            {showAll ? `Show less` : `Show ${urls.length - maxUrlsToShow} more`}
                                          </button>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        }) || <span className="no-data">No citations found</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => {
                  setShowResults(false);
                  setShowAnalysis(false);
                  setCurrentStep(1);
                  setFormData({ businessName: '', zipCode: '' });
                  setUserQueries([]);
                  setAnalysisResults(null);
                  setShowProfileBubbles(false);
                }}
                className="restart-button"
              >
                Start New Analysis
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Payment Modal */}
      {showPaymentModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '16px',
              color: '#1a1a1a'
            }}>
              Unlock Premium Features
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              {modalContent.feature} requires a small fee to unlock. Upgrade to {modalContent.functionality}.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowPaymentModal(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: 'white',
                  color: '#666',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#f5f5f5'}
                onMouseOut={(e) => e.target.style.background = 'white'}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  // Store business context for post-checkout flow
                  sessionStorage.setItem('businessData', JSON.stringify(businessData));
                  navigate('/checkout');
                }}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontWeight: '500'
                }}
                onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paywall Overlay - Appears after 7 seconds in analysis view */}
      {showPaywallOverlay && (
        <div className="paywall-overlay">
          <div className="paywall-content">
            <div className="paywall-icon">🔒</div>
            <h2 className="paywall-title">Unlock Your Report</h2>
            <p className="paywall-description">
              View complete AI responses across all platforms, competitor rankings, and how to improve your AI presence.
            </p>
            <ul className="paywall-features">
              <li>✓ View all AI chats and chatbot responses</li>
              <li>✓ See what AI is saying about your business</li>
              <li>✓ Know where AI finds its information</li>
              <li>✓ Analyze competitor mentions and rankings</li>
              <li>✓ Custom tips to improve your AI presence</li>
            </ul>
            <button
              onClick={() => {
                // Store business context for post-checkout flow
                sessionStorage.setItem('businessData', JSON.stringify(businessData));
                navigate('/checkout');
              }}
              className="paywall-cta"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiStepForm;